import {
  Button,
  Divider,
  TextField,
  toast,
  Tooltip,
  Typography,
} from "@suraasa/placebo-ui"
import { useMutation, useQuery } from "@tanstack/react-query"
import api from "api"
import { SavedAddress } from "api/resources/payment/types"
import { Product } from "api/resources/products/types"
import AsyncBuilder from "components/AsyncBuilder"
import ErrorPage from "components/ErrorPage"
import { AuthManager } from "features/Authentication/AuthManager"
import { useCPATPayment } from "features/Payment/CartlessPaymentFlow/useCPATPayment"
import { useGlobalContext } from "global/Context/context"
import isNil from "lodash/isNil"
import { useEffect, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { Link, useSearchParams } from "react-router-dom"
import { getPlatformURL, mapErrorsV2 } from "utils/helpers"
import useFormatPrice from "utils/hooks/useFormatPrice"

type Price = {
  currency: {
    code: string
    symbol: string
  }
  itemPrice: number
  finalAmount: number
  discountAmount: number

  taxes: { amount: number; name: string }[]
}

interface OrderSummaryProps {
  address: SavedAddress | null
}

const getPurchaseDetails = (
  product: Product | undefined,
  price: Price | undefined | null
) => {
  if (!product || !price) return null

  const details = {
    itemName: product.name,
    amount: price.finalAmount,
    currency: {
      code: price.currency.code,
      symbol: price.currency.symbol,
    },
  }

  return details
}

type Promo = {
  code: string
}

const OrderSummary = ({ address }: OrderSummaryProps) => {
  const [searchParams] = useSearchParams()
  const formatPrice = useFormatPrice()

  // Don't use this state directly. Use priceDisplay instead
  const [_priceWithTax, _setPriceWithTax] = useState<Price | null>()
  const [productAvailable, setProductAvailable] = useState(true)
  const [promo, setPromo] = useState<Promo | null>(null)

  const freePromos = ["GROWTH", "GUESS2025", "DIDAC2025"]

  const {
    register,
    handleSubmit,
    reset,
    setError,
    getValues,
    setValue,
    formState: { errors },
  } = useForm<{ promocode: string }>()

  const onSubmit = handleSubmit(async data => {
    await applyPromocode(getOrderId() || "", data.promocode)
  })

  const orderIdRef = useRef<string | null>(null)

  const { isAuthenticated } = useGlobalContext()

  const productSlug = searchParams.get("product")

  const authInfo = AuthManager.getPlatformAuth()

  const applyPromocode = async (orderId: string, promocode: string | null) => {
    try {
      const res = await api.orders.applyPromocode({
        urlParams: {
          orderId,
        },
        data: {
          promoCode: promocode,
        },
      })
      _setPriceWithTax({
        itemPrice: res.itemTotal,
        finalAmount: res.finalAmount,
        discountAmount: res.discountAmount,
        taxes: res.taxes,
        currency: res.currency,
      })

      if (promocode) {
        setPromo({ code: promocode })
        toast.success("Promocode applied!")
      } else {
        reset()
        setPromo(null)
        toast.info("Promocode removed")
      }
    } catch (e) {
      mapErrorsV2(e, { setter: setError, fields: [["promoCode", "promocode"]] })
    }
  }

  // Gives prices without tax (auto-detects country based on IP)
  const product = useQuery({
    queryFn: () =>
      api.products.getProduct({
        urlParams: { productSlug: productSlug || "" },
        params: {
          fields: ["prices"],
        },
      }),
    queryKey: ["product", productSlug],
  })

  const showFrontendDiscount = freePromos
    .map(code => code.toLowerCase())
    .includes(
      (
        searchParams.get("promo") ||
        searchParams.get("promocode") ||
        ""
      ).toLowerCase()
    )

  const getOrderId = () => {
    return orderIdRef.current
  }

  const payment = useCPATPayment({
    purchaseDetails: getPurchaseDetails(product.data, _priceWithTax),
  })

  const getPricesWithTax = useMutation({
    mutationFn: () =>
      api.products.getProductTax({
        data: {
          product_slug: productSlug,
          country_code: address?.country?.isoCode,
          state_code: address?.state?.isoCode,
        },
      }),
    onSuccess: async data => {
      if (!data.taxes) {
        setProductAvailable(false)
      } else {
        setProductAvailable(true)

        const code = searchParams.get("promo") || searchParams.get("promocode")

        if (code) {
          setValue("promocode", code)
          if (orderIdRef.current) {
            await applyPromocode(orderIdRef.current, code)
          }
        }
      }
    },
  })

  useEffect(() => {
    if (authInfo?.user.email && address && productSlug) {
      getPricesWithTax.mutate()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [address, productSlug, authInfo?.user.email])

  const priceDisplay = (() => {
    // priceWithTax
    if (_priceWithTax) {
      if (_priceWithTax.finalAmount == null) {
        return "Unavailable"
      }
      return (
        _priceWithTax.currency.symbol + formatPrice(_priceWithTax.finalAmount)
      )
    }

    // priceWithoutTax (Initial display when country is not selected)
    if (product.data) {
      if (product.data.prices == null || product.data.prices.price == null) {
        return "Unavailable"
      }
      return (
        product.data.prices.currency.symbol +
        formatPrice(product.data.prices.price)
      )
    }

    return "Not available in your region"
  })()

  const enablePayNow = isAuthenticated && Boolean(address)

  useEffect(() => {
    const createOrder = async () => {
      if (!address?.uuid || !productSlug) {
        throw new Error("billingAddressId or productSlug cannot be null")
      }
      const res = await payment.createOrder(address.uuid, productSlug)

      if (res) {
        orderIdRef.current = res.id

        const code =
          searchParams.get("promo") ||
          searchParams.get("promocode") ||
          res.promoCode ||
          getValues("promocode")

        if (code) {
          setValue("promocode", code)
          await applyPromocode(res.id, code)
        } else {
          _setPriceWithTax({
            currency: res.currency,
            itemPrice: res.finalAmount,
            finalAmount: res.finalAmount,
            discountAmount: 0,
            taxes: res.taxes,
          })
        }
      }
    }

    if (enablePayNow) {
      createOrder()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enablePayNow, address])

  useEffect(() => {
    const code = searchParams.get("promo") || searchParams.get("promocode")
    if (productAvailable && code) {
      setValue("promocode", code)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productAvailable])

  return (
    <>
      {payment.stripePaymentElement}
      {productAvailable ? (
        <div className="mb-10 h-full px-2 pt-9 md:mx-auto md:max-w-md">
          <Typography variant="title4" className="mb-4">
            Order Summary
          </Typography>
          {promo !== null ? (
            <div className="mb-4 flex items-center justify-between">
              <Typography variant="strong">{promo.code}</Typography>
              <Button
                variant="text"
                onClick={() => applyPromocode(getOrderId() || "", null)}
                color="critical"
              >
                Remove
              </Button>
            </div>
          ) : (
            <Tooltip
              enabled={!enablePayNow}
              content="Please add a billing address first"
            >
              <form onSubmit={onSubmit}>
                <div className="mb-4 flex items-center justify-between">
                  <TextField
                    errors={errors.promocode?.message}
                    helperText={errors.promocode?.message}
                    className="me-1.5"
                    placeholder="Promocode"
                    {...register("promocode", {
                      required: "Required",
                    })}
                    disabled={!enablePayNow}
                  />
                  <Button
                    color="black"
                    type="submit"
                    className="border-2 border-black-500 px-2 py-1.25"
                    variant="text"
                    disabled={!enablePayNow}
                  >
                    Apply
                  </Button>
                </div>
              </form>
            </Tooltip>
          )}

          <div className="mb-2 flex flex-row justify-between">
            <Typography variant={"body"} className="text-onSurface-600">
              Item
            </Typography>
            {_priceWithTax && (
              <Typography variant={"strong"} className="text-onSurface-600">
                {_priceWithTax.currency.symbol}
                {formatPrice(_priceWithTax.itemPrice)}
              </Typography>
            )}
            {!_priceWithTax && product.isSuccess && (
              <Typography variant={"strong"} className="text-onSurface-600">
                {priceDisplay}
              </Typography>
            )}
          </div>

          <div className="mb-2 flex flex-col gap-2">
            {_priceWithTax &&
              _priceWithTax.taxes.map(tax => (
                <div key={tax.name} className="flex flex-row justify-between">
                  <Typography variant={"body"} className="text-onSurface-600">
                    {tax.name}
                  </Typography>
                  <Typography variant={"strong"} className="text-onSurface-600">
                    {_priceWithTax.currency.symbol}
                    {formatPrice(tax.amount)}
                  </Typography>
                </div>
              ))}
          </div>

          {!isNil(_priceWithTax?.discountAmount) &&
            _priceWithTax.discountAmount > 0 && (
              <div className="mb-2 flex flex-row justify-between">
                <Typography variant={"body"} className="text-onSurface-600">
                  Discount
                </Typography>
                <Typography variant={"strong"} className="text-onSurface-600">
                  {_priceWithTax.currency.symbol}
                  {formatPrice(_priceWithTax.discountAmount)}
                </Typography>
              </div>
            )}

          {!_priceWithTax && showFrontendDiscount && (
            <div className="mb-2 flex flex-row justify-between">
              <Typography variant={"body"} className="text-onSurface-600">
                Discount
              </Typography>
              <Typography variant={"strong"} className="text-onSurface-600">
                {priceDisplay}
              </Typography>
            </div>
          )}

          <Divider weight="light" className="mb-2" />
          {product.isSuccess && (
            <div className="mb-5 flex flex-row justify-between">
              <Typography variant="strong">Total</Typography>
              <Typography variant={"title2"}>
                {showFrontendDiscount
                  ? `${
                      _priceWithTax?.currency.symbol ||
                      product.data.prices?.currency.symbol
                    }0`
                  : priceDisplay}
              </Typography>
            </div>
          )}

          <Tooltip
            enabled={!enablePayNow}
            content={"Please complete your billing details first"}
          >
            <AsyncBuilder
              handler={async () => {
                await payment.checkout(promo?.code || null)
              }}
              render={({ onClick, loading }) => (
                <Button
                  color="black"
                  label="Access my CPAT"
                  className="mb-2 w-full"
                  onClick={onClick}
                  disabled={
                    !enablePayNow || !address || product.isError || !productSlug
                  }
                  loading={loading}
                />
              )}
            />
          </Tooltip>

          <Typography
            variant={"smallBody"}
            className="text-center text-secondary-500"
          >
            By proceeding you agree to accept our
            <Link
              to={getPlatformURL("suraasa", "/terms-of-use")}
              target="_blank"
            >
              <span className="text-interactive-500"> terms & conditions</span>
            </Link>
          </Typography>
        </div>
      ) : (
        <ErrorPage
          className="mt-10 min-h-[auto]"
          errorStatus={404}
          title="Product not available"
          description="This product is not available in your region."
        />
      )}
    </>
  )
}

export default OrderSummary
